import { usePathname } from "next/navigation";
import { forwardRef, useEffect, useRef, useState } from "react";
import CloseIcon from "@/assets/icons/close.svg";
import Logo from "@/assets/icons/white-square-logo.svg";
import { Img } from "@/components/image";
import { Button } from "@/components/ui/button";
import type { MobileAppTopBannerType } from "@/features/top-banners/types/mobile-app-top-banner.types";
import { useCheckPlatform } from "@/lib/hooks/use-check-platform";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";
import useTopBannersStore from "../store/top-banners-store";

export const TopBannerMobileApp = forwardRef(
  (
    {
      banner,
      onClose,
    }: { banner: MobileAppTopBannerType; onClose: () => void },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const uuid = `mobile-app-banner-${banner.id}`;
    const textKey = `${banner.titleDesktop}|${banner.description}`;
    const [show, setShow] = useState(() => {
      if (typeof window === "undefined") return true;

      const existingClosed = JSON.parse(sessionStorage.getItem("tb") || "[]");
      const storedBanners = JSON.parse(
        sessionStorage.getItem("tbData") || "{}",
      );

      if (existingClosed.includes(uuid)) {
        const storedText = storedBanners[uuid];
        if (storedText !== textKey) {
          const updatedClosed = existingClosed.filter(
            (id: string) => id !== uuid,
          );
          delete storedBanners[uuid];

          sessionStorage.setItem("tb", JSON.stringify(updatedClosed));
          sessionStorage.setItem("tbData", JSON.stringify(storedBanners));
          return true;
        }
        return false;
      }

      return true;
    });

    const pathname = usePathname();
    const { isMobile } = useMobile();
    const totalHeight = useTopBannersStore((state) => state.totalHeight);
    const prevPathnameRef = useRef(pathname);
    const scrollAmount = totalHeight + (isMobile ? 80 : 160);

    const platform = useCheckPlatform();

    const link = platform === "android" ? banner.android_link : banner.ios_link;
    const img =
      platform === "android" ? banner.android_image : banner.ios_image;

    const handleClose = () => {
      setShow(false);
      if (typeof window !== "undefined") {
        const existingClosed = JSON.parse(sessionStorage.getItem("tb") || "[]");
        const storedBanners = JSON.parse(
          sessionStorage.getItem("tbData") || "{}",
        );

        if (storedBanners[uuid] && storedBanners[uuid] !== textKey) {
          const updatedClosed = existingClosed.filter(
            (id: string) => id !== uuid,
          );
          delete storedBanners[uuid];

          sessionStorage.setItem("tb", JSON.stringify(updatedClosed));
          sessionStorage.setItem("tbData", JSON.stringify(storedBanners));
        }

        storedBanners[uuid] = textKey;
        sessionStorage.setItem("tbData", JSON.stringify(storedBanners));

        if (!existingClosed.includes(uuid)) {
          existingClosed.push(uuid);
          sessionStorage.setItem("tb", JSON.stringify(existingClosed));
        }
      }
    };

    useEffect(() => {
      if (prevPathnameRef.current !== pathname) {
        const shouldScroll = sessionStorage.getItem("bannerScrollNeeded");
        if (shouldScroll === "true") {
          sessionStorage.removeItem("bannerScrollNeeded");
          setTimeout(() => {
            window.scrollBy({ top: scrollAmount, behavior: "smooth" });
          }, 50);
        }
        prevPathnameRef.current = pathname;
      }
    }, [pathname, scrollAmount]);

    return (
      <div
        className={cn(
          "flex min-h-[64px] items-center bg-brand-green1-300 py-2 transition-[height] md:hidden",
          !show && "hidden",
        )}
        data-uuid={uuid}
        ref={ref}
      >
        <div className="container flex items-center justify-between gap-2 max-md:px-1 md:gap-0 md:pr-0">
          <div className="flex items-center gap-2 md:w-full">
            {/* Mobile */}
            <Button
              className="min-h-9 w-9 p-0 text-white md:hidden"
              onClick={() => {
                onClose();
                handleClose();
              }}
              type="button"
              variant="ghost"
            >
              <CloseIcon className="text-white" />
            </Button>
            <Logo className="shrink-0" />
            <p className="hidden font-medium text-2xl text-brand-white md:block">
              {banner.titleDesktop}
            </p>
            <div className="shrink-0 md:hidden">
              <p className="font-bold text-brand-white text-sm leading-4">
                {banner.titleMobile}
              </p>
              <p className="text-brand-white text-xs">{banner.description}</p>
            </div>
          </div>
          {img?.url && (
            <a
              className="order-2"
              href={link}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Img
                alt={img.name || banner.titleMobile}
                className="w-[110px] xs:w-[130px] md:hidden"
                height={img.height}
                src={img.url}
                width={img.width}
              />
            </a>
          )}
          {/* Desktop */}
          <Button
            className="hidden min-h-9 w-9 p-0 text-white md:block"
            onClick={() => {
              onClose();
              handleClose();
            }}
            type="button"
            variant="ghost"
          >
            <CloseIcon className="text-white" />
          </Button>
        </div>
      </div>
    );
  },
);
